/**
 * Onboarding preset configurations for space, asset, and job suggestions.
 * These presets are industry-specific and align with backend industry presets.
 *
 * Backend source: @gfactor/server/src/config/industryPresets.ts
 */

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBed,
  faBeerMugEmpty,
  faBolt,
  faBoxes,
  faBreadSlice,
  faBroom,
  faBuilding,
  faBuildingUser,
  faCar,
  faCogs,
  faCouch,
  faDesktop,
  faDoorOpen,
  faFan,
  faFaucet,
  faFire,
  faFireBurner,
  faGlassWater,
  faHardHat,
  faHome,
  faHotel,
  faHouse,
  faHouseChimneyWindow,
  faIndustry,
  faKitchenSet,
  faLightbulb,
  faLocationDot,
  faMugHot,
  faPlateUtensils,
  faPlus,
  faPumpSoap,
  faSatelliteDish,
  faServer,
  faShieldAlt,
  faShower,
  faSink,
  faSnowflake,
  faStore,
  faSwimmingPool,
  faToolbox,
  faTrailer,
  faTree,
  faTruck,
  faUmbrellaBeach,
  faUtensils,
  faWarehouse,
  faWater,
  faWrench,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBlender,
  faOilCan,
  faTshirt,
} from '@fortawesome/pro-solid-svg-icons';

// =============================================================================
// TYPES
// =============================================================================

export type IndustryName =
  | 'Holiday Rentals & Property Management'
  | 'Cleaning & Laundry Services'
  | 'Facilities Maintenance'
  | 'Trades & Construction'
  | 'Industrial'
  | 'Fleet & Transport'
  | 'Food & Beverage'
  | 'Other Industry';

export type SpaceSuggestion = {
  name: string;
  icon: IconDefinition;
};

export type AssetSuggestion = {
  name: string;
  type: string; // Must match backend assetTypes
  icon: IconDefinition;
};

export type JobSuggestion = {
  name: string;
  defaultFreq: 'daily' | 'weekly' | 'monthly';
  defaultInterval?: string;
  description?: string;
  /** Optional checklist items to embed in each task (rendered as Draft.js checklist-item blocks). */
  checklist?: string[];
};

export type IndustryOnboardingConfig = {
  spaces: SpaceSuggestion[];
  assets: AssetSuggestion[];
  jobs: JobSuggestion[];
};

// =============================================================================
// INDUSTRY CONFIGURATIONS
// =============================================================================

export const ONBOARDING_PRESETS: Record<
  IndustryName,
  IndustryOnboardingConfig
> = {
  // ---------------------------------------------------------------------------
  // HOLIDAY RENTALS & PROPERTY MANAGEMENT
  // ---------------------------------------------------------------------------
  'Holiday Rentals & Property Management': {
    spaces: [
      { name: 'Kitchen', icon: faUtensils as IconDefinition },
      { name: 'Bedroom 1', icon: faBed as IconDefinition },
      { name: 'Bedroom 2', icon: faBed as IconDefinition },
      { name: 'Bathroom', icon: faShower as IconDefinition },
      { name: 'Living Room', icon: faCouch as IconDefinition },
      { name: 'Laundry', icon: faTshirt as IconDefinition },
      { name: 'Outdoor Area', icon: faTree as IconDefinition },
      { name: 'Garage', icon: faCar as IconDefinition },
      { name: 'Pool Area', icon: faSwimmingPool as IconDefinition },
      { name: 'Entryway', icon: faDoorOpen as IconDefinition },
    ],
    assets: [
      // Appliances
      {
        name: 'Dishwasher',
        type: 'Appliances',
        icon: faBlender as IconDefinition,
      },
      {
        name: 'Washing Machine',
        type: 'Appliances',
        icon: faTshirt as IconDefinition,
      },
      { name: 'Dryer', type: 'Appliances', icon: faFan as IconDefinition },
      {
        name: 'Refrigerator',
        type: 'Appliances',
        icon: faBlender as IconDefinition,
      },
      { name: 'Oven', type: 'Appliances', icon: faUtensils as IconDefinition },
      {
        name: 'Microwave',
        type: 'Appliances',
        icon: faUtensils as IconDefinition,
      },
      // Hot water systems
      {
        name: 'Hot Water System',
        type: 'Hot water systems',
        icon: faWater as IconDefinition,
      },
      // Smoke alarms
      {
        name: 'Smoke Alarm',
        type: 'Smoke alarms',
        icon: faFire as IconDefinition,
      },
      // Pools & spas
      {
        name: 'Pool Pump',
        type: 'Pools & spas',
        icon: faSwimmingPool as IconDefinition,
      },
      {
        name: 'Spa Heater',
        type: 'Pools & spas',
        icon: faSwimmingPool as IconDefinition,
      },
      // Outdoor equipment
      {
        name: 'BBQ',
        type: 'Outdoor equipment',
        icon: faTree as IconDefinition,
      },
      {
        name: 'Lawn Mower',
        type: 'Outdoor equipment',
        icon: faTree as IconDefinition,
      },
      // Doors & Locks
      {
        name: 'Smart Lock',
        type: 'Doors & Locks',
        icon: faDoorOpen as IconDefinition,
      },
      // Internet / IT
      {
        name: 'WiFi Router',
        type: 'Internet / IT equipment',
        icon: faServer as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Turnover Clean',
        defaultFreq: 'weekly',
        description: 'Clean between guests',
      },
      {
        name: 'Smoke Alarm Inspection',
        defaultFreq: 'monthly',
        description: 'Test and maintain smoke alarms',
      },
      {
        name: 'Pool Service',
        defaultFreq: 'weekly',
        description: 'Clean and balance pool chemicals',
      },
      {
        name: 'Linen Change',
        defaultFreq: 'weekly',
        description: 'Replace bed linens and towels',
      },
      {
        name: 'Deep Clean',
        defaultFreq: 'monthly',
        description: 'Thorough property cleaning',
      },
      {
        name: 'Appliance Check',
        defaultFreq: 'monthly',
        description: 'Inspect and maintain appliances',
      },
      {
        name: 'Hot Water Service',
        defaultFreq: 'monthly',
        description: 'Service hot water system',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // CLEANING & LAUNDRY SERVICES
  // ---------------------------------------------------------------------------
  'Cleaning & Laundry Services': {
    spaces: [
      { name: 'Main Floor', icon: faBuilding as IconDefinition },
      { name: 'Storage Room', icon: faBoxes as IconDefinition },
      { name: 'Equipment Room', icon: faToolbox as IconDefinition },
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Loading Bay', icon: faTruck as IconDefinition },
      { name: 'Laundry Room', icon: faTshirt as IconDefinition },
      { name: 'Chemical Storage', icon: faWarehouse as IconDefinition },
      { name: 'Staff Room', icon: faCouch as IconDefinition },
      { name: 'Reception', icon: faDoorOpen as IconDefinition },
      { name: 'Vehicle Bay', icon: faCar as IconDefinition },
    ],
    assets: [
      // Cleaning equipment
      {
        name: 'Vacuum Cleaner',
        type: 'Cleaning equipment',
        icon: faBroom as IconDefinition,
      },
      {
        name: 'Floor Scrubber',
        type: 'Cleaning equipment',
        icon: faBroom as IconDefinition,
      },
      {
        name: 'Steam Cleaner',
        type: 'Cleaning equipment',
        icon: faBroom as IconDefinition,
      },
      {
        name: 'Pressure Washer',
        type: 'Cleaning equipment',
        icon: faWater as IconDefinition,
      },
      {
        name: 'Carpet Extractor',
        type: 'Cleaning equipment',
        icon: faBroom as IconDefinition,
      },
      {
        name: 'Polisher',
        type: 'Cleaning equipment',
        icon: faBroom as IconDefinition,
      },
      // Laundry machines
      {
        name: 'Commercial Washer',
        type: 'Laundry machines',
        icon: faTshirt as IconDefinition,
      },
      {
        name: 'Commercial Dryer',
        type: 'Laundry machines',
        icon: faFan as IconDefinition,
      },
      {
        name: 'Ironing Press',
        type: 'Laundry machines',
        icon: faTshirt as IconDefinition,
      },
      {
        name: 'Folding Machine',
        type: 'Laundry machines',
        icon: faTshirt as IconDefinition,
      },
      {
        name: 'Dry Cleaning Unit',
        type: 'Laundry machines',
        icon: faTshirt as IconDefinition,
      },
      // Vehicles
      {
        name: 'Service Van',
        type: 'Vehicles',
        icon: faTruck as IconDefinition,
      },
      {
        name: 'Delivery Vehicle',
        type: 'Vehicles',
        icon: faCar as IconDefinition,
      },
      // Storage equipment
      {
        name: 'Linen Cart',
        type: 'Storage equipment',
        icon: faBoxes as IconDefinition,
      },
      {
        name: 'Supply Trolley',
        type: 'Storage equipment',
        icon: faBoxes as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Machine Service',
        defaultFreq: 'monthly',
        description: 'Service washers and dryers',
      },
      {
        name: 'Filter Clean',
        defaultFreq: 'weekly',
        description: 'Clean lint traps and filters',
      },
      {
        name: 'Deep Equipment Clean',
        defaultFreq: 'monthly',
        description: 'Thorough equipment cleaning',
      },
      {
        name: 'Vehicle Service',
        defaultFreq: 'monthly',
        description: 'Service company vehicles',
      },
      {
        name: 'Drum Descale',
        defaultFreq: 'monthly',
        description: 'Descale washer drums',
      },
      {
        name: 'Belt & Bearing Check',
        defaultFreq: 'monthly',
        description: 'Inspect mechanical parts',
      },
      {
        name: 'Chemical Stock Check',
        defaultFreq: 'weekly',
        description: 'Restock cleaning supplies',
      },
      {
        name: 'Safety Inspection',
        defaultFreq: 'monthly',
        description: 'Check safety equipment',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // FACILITIES MAINTENANCE
  // ---------------------------------------------------------------------------
  'Facilities Maintenance': {
    spaces: [
      { name: 'Reception', icon: faDoorOpen as IconDefinition },
      { name: 'Meeting Room', icon: faBuilding as IconDefinition },
      { name: 'Amenities', icon: faShower as IconDefinition },
      { name: 'Plant Room', icon: faCogs as IconDefinition },
      { name: 'Storeroom', icon: faBoxes as IconDefinition },
      { name: 'Common Area', icon: faCouch as IconDefinition },
      { name: 'Kitchen', icon: faUtensils as IconDefinition },
      { name: 'Bathroom', icon: faShower as IconDefinition },
      { name: 'Server Room', icon: faServer as IconDefinition },
      { name: 'Car Park', icon: faCar as IconDefinition },
    ],
    assets: [
      // HVAC units
      {
        name: 'Air Conditioner',
        type: 'HVAC units',
        icon: faFan as IconDefinition,
      },
      { name: 'Heater', type: 'HVAC units', icon: faFire as IconDefinition },
      {
        name: 'Ventilation Fan',
        type: 'HVAC units',
        icon: faFan as IconDefinition,
      },
      // Lighting & Electrical
      {
        name: 'Lighting Panel',
        type: 'Lighting & Electrical',
        icon: faLightbulb as IconDefinition,
      },
      {
        name: 'Emergency Lighting',
        type: 'Lighting & Electrical',
        icon: faLightbulb as IconDefinition,
      },
      {
        name: 'Switchboard',
        type: 'Lighting & Electrical',
        icon: faBolt as IconDefinition,
      },
      // Plumbing fixtures
      {
        name: 'Hot Water System',
        type: 'Plumbing fixtures',
        icon: faFaucet as IconDefinition,
      },
      {
        name: 'Water Pump',
        type: 'Plumbing fixtures',
        icon: faWater as IconDefinition,
      },
      // Fire safety equipment
      {
        name: 'Fire Extinguisher',
        type: 'Fire safety equipment',
        icon: faFire as IconDefinition,
      },
      {
        name: 'Fire Panel',
        type: 'Fire safety equipment',
        icon: faShieldAlt as IconDefinition,
      },
      {
        name: 'Sprinkler System',
        type: 'Fire safety equipment',
        icon: faWater as IconDefinition,
      },
      // Doors & Access systems
      {
        name: 'Access Control',
        type: 'Doors & Access systems',
        icon: faDoorOpen as IconDefinition,
      },
      {
        name: 'Automatic Door',
        type: 'Doors & Access systems',
        icon: faDoorOpen as IconDefinition,
      },
      {
        name: 'Security Gate',
        type: 'Doors & Access systems',
        icon: faShieldAlt as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'HVAC Filter Change',
        defaultFreq: 'monthly',
        description: 'Replace air filters',
      },
      {
        name: 'Fire Safety Check',
        defaultFreq: 'monthly',
        description: 'Test fire equipment',
      },
      {
        name: 'Lighting Inspection',
        defaultFreq: 'monthly',
        description: 'Check and replace bulbs',
      },
      {
        name: 'Plumbing Check',
        defaultFreq: 'monthly',
        description: 'Inspect pipes and fixtures',
      },
      {
        name: 'Access System Test',
        defaultFreq: 'weekly',
        description: 'Test door and gate systems',
      },
      {
        name: 'Emergency Light Test',
        defaultFreq: 'monthly',
        description: 'Test emergency lighting',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // TRADES & CONSTRUCTION
  // ---------------------------------------------------------------------------
  'Trades & Construction': {
    spaces: [
      { name: 'Workshop', icon: faToolbox as IconDefinition },
      { name: 'Tool Storage', icon: faWrench as IconDefinition },
      { name: 'Vehicle Bay', icon: faTruck as IconDefinition },
      { name: 'Parts Room', icon: faBoxes as IconDefinition },
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Loading Dock', icon: faTruck as IconDefinition },
      { name: 'Equipment Yard', icon: faHardHat as IconDefinition },
      { name: 'Fabrication Area', icon: faCogs as IconDefinition },
      { name: 'Electrical Room', icon: faBolt as IconDefinition },
      { name: 'Paint Booth', icon: faOilCan as IconDefinition },
    ],
    assets: [
      // Power tools
      { name: 'Drill', type: 'Power tools', icon: faWrench as IconDefinition },
      {
        name: 'Circular Saw',
        type: 'Power tools',
        icon: faWrench as IconDefinition,
      },
      {
        name: 'Angle Grinder',
        type: 'Power tools',
        icon: faWrench as IconDefinition,
      },
      {
        name: 'Impact Driver',
        type: 'Power tools',
        icon: faWrench as IconDefinition,
      },
      // Vehicles
      { name: 'Work Ute', type: 'Vehicles', icon: faTruck as IconDefinition },
      { name: 'Van', type: 'Vehicles', icon: faTruck as IconDefinition },
      // Trailers
      {
        name: 'Tool Trailer',
        type: 'Trailers',
        icon: faTrailer as IconDefinition,
      },
      {
        name: 'Equipment Trailer',
        type: 'Trailers',
        icon: faTrailer as IconDefinition,
      },
      // Heavy equipment
      {
        name: 'Excavator',
        type: 'Heavy equipment',
        icon: faIndustry as IconDefinition,
      },
      {
        name: 'Bobcat',
        type: 'Heavy equipment',
        icon: faIndustry as IconDefinition,
      },
      // Compressors / Generators
      {
        name: 'Air Compressor',
        type: 'Compressors / Generators',
        icon: faCogs as IconDefinition,
      },
      {
        name: 'Generator',
        type: 'Compressors / Generators',
        icon: faBolt as IconDefinition,
      },
      // Ladders / Access gear
      {
        name: 'Extension Ladder',
        type: 'Ladders / Access gear',
        icon: faHardHat as IconDefinition,
      },
      {
        name: 'Scaffold Set',
        type: 'Ladders / Access gear',
        icon: faHardHat as IconDefinition,
      },
      // Safety Equipment
      {
        name: 'First Aid Kit',
        type: 'Safety Equipment',
        icon: faShieldAlt as IconDefinition,
      },
      {
        name: 'Fall Arrest Kit',
        type: 'Safety Equipment',
        icon: faShieldAlt as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Tool Service',
        defaultFreq: 'monthly',
        description: 'Service power tools',
      },
      {
        name: 'Vehicle Service',
        defaultFreq: 'monthly',
        description: 'Service work vehicles',
      },
      {
        name: 'Trailer Inspection',
        defaultFreq: 'monthly',
        description: 'Safety inspection',
      },
      {
        name: 'Compressor Service',
        defaultFreq: 'monthly',
        description: 'Service air compressor',
      },
      {
        name: 'Generator Service',
        defaultFreq: 'monthly',
        description: 'Service generator',
      },
      {
        name: 'Safety Gear Check',
        defaultFreq: 'weekly',
        description: 'Check safety equipment',
      },
      {
        name: 'Ladder Inspection',
        defaultFreq: 'monthly',
        description: 'Safety check ladders',
      },
      {
        name: 'Heavy Equipment Service',
        defaultFreq: 'monthly',
        description: 'Service machinery',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // INDUSTRIAL
  // ---------------------------------------------------------------------------
  Industrial: {
    spaces: [
      { name: 'Production Line', icon: faIndustry as IconDefinition },
      { name: 'Welding Bay', icon: faFire as IconDefinition },
      { name: 'Tooling Room', icon: faToolbox as IconDefinition },
      { name: 'QC Area', icon: faShieldAlt as IconDefinition },
      { name: 'Loading Bay', icon: faTruck as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Plant Room', icon: faCogs as IconDefinition },
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Maintenance Area', icon: faWrench as IconDefinition },
      { name: 'Spray Booth', icon: faOilCan as IconDefinition },
    ],
    assets: [
      // Machinery
      {
        name: 'CNC Machine',
        type: 'Machinery',
        icon: faCogs as IconDefinition,
      },
      { name: 'Lathe', type: 'Machinery', icon: faCogs as IconDefinition },
      { name: 'Press', type: 'Machinery', icon: faCogs as IconDefinition },
      // Heavy equipment
      {
        name: 'Forklift',
        type: 'Heavy equipment',
        icon: faTruck as IconDefinition,
      },
      {
        name: 'Crane',
        type: 'Heavy equipment',
        icon: faIndustry as IconDefinition,
      },
      // Welders
      { name: 'MIG Welder', type: 'Welders', icon: faFire as IconDefinition },
      { name: 'TIG Welder', type: 'Welders', icon: faFire as IconDefinition },
      // Hoists
      {
        name: 'Overhead Hoist',
        type: 'Hoists',
        icon: faIndustry as IconDefinition,
      },
      {
        name: 'Chain Block',
        type: 'Hoists',
        icon: faIndustry as IconDefinition,
      },
      // Compressors / Generators
      {
        name: 'Industrial Compressor',
        type: 'Compressors / Generators',
        icon: faCogs as IconDefinition,
      },
      {
        name: 'Backup Generator',
        type: 'Compressors / Generators',
        icon: faBolt as IconDefinition,
      },
      // Industrial pumps
      {
        name: 'Hydraulic Pump',
        type: 'Industrial pumps',
        icon: faWater as IconDefinition,
      },
      {
        name: 'Coolant Pump',
        type: 'Industrial pumps',
        icon: faWater as IconDefinition,
      },
      // Safety equipment
      {
        name: 'Safety Shower',
        type: 'Safety equipment',
        icon: faShieldAlt as IconDefinition,
      },
      {
        name: 'Eye Wash Station',
        type: 'Safety equipment',
        icon: faShieldAlt as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Machine Calibration',
        defaultFreq: 'weekly',
        description: 'Calibrate precision machinery',
      },
      {
        name: 'Preventive Maintenance',
        defaultFreq: 'monthly',
        description: 'Scheduled maintenance',
      },
      {
        name: 'Forklift Service',
        defaultFreq: 'monthly',
        description: 'Service forklifts',
      },
      {
        name: 'Welder Inspection',
        defaultFreq: 'weekly',
        description: 'Check welding equipment',
      },
      {
        name: 'Hoist Test',
        defaultFreq: 'monthly',
        description: 'Load test hoists',
      },
      {
        name: 'Safety Equipment Check',
        defaultFreq: 'weekly',
        description: 'Inspect safety stations',
      },
      {
        name: 'Compressor Service',
        defaultFreq: 'monthly',
        description: 'Service compressors',
      },
      {
        name: 'Lubrication Round',
        defaultFreq: 'weekly',
        description: 'Lubricate equipment',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // FLEET & TRANSPORT
  // ---------------------------------------------------------------------------
  'Fleet & Transport': {
    spaces: [
      { name: 'Workshop Bay 1', icon: faToolbox as IconDefinition },
      { name: 'Workshop Bay 2', icon: faToolbox as IconDefinition },
      { name: 'Parts Storage', icon: faBoxes as IconDefinition },
      { name: 'Fleet Yard', icon: faTruck as IconDefinition },
      { name: 'Wash Bay', icon: faWater as IconDefinition },
      { name: 'Fuel Station', icon: faOilCan as IconDefinition },
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Driver Room', icon: faCouch as IconDefinition },
      { name: 'Trailer Park', icon: faTrailer as IconDefinition },
      { name: 'Tyre Storage', icon: faCar as IconDefinition },
    ],
    assets: [
      // Vehicles
      { name: 'Truck', type: 'Vehicles', icon: faTruck as IconDefinition },
      { name: 'Van', type: 'Vehicles', icon: faTruck as IconDefinition },
      { name: 'Ute', type: 'Vehicles', icon: faCar as IconDefinition },
      { name: 'Bus', type: 'Vehicles', icon: faTruck as IconDefinition },
      { name: 'Forklift', type: 'Vehicles', icon: faTruck as IconDefinition },
      // Trailers
      {
        name: 'Box Trailer',
        type: 'Trailers',
        icon: faTrailer as IconDefinition,
      },
      {
        name: 'Flatbed Trailer',
        type: 'Trailers',
        icon: faTrailer as IconDefinition,
      },
      {
        name: 'Refrigerated Trailer',
        type: 'Trailers',
        icon: faTrailer as IconDefinition,
      },
      { name: 'Tanker', type: 'Trailers', icon: faTrailer as IconDefinition },
      // Tools & Equipment
      {
        name: 'Diagnostic Tool',
        type: 'Tools & Equipment',
        icon: faWrench as IconDefinition,
      },
      {
        name: 'Jack Set',
        type: 'Tools & Equipment',
        icon: faWrench as IconDefinition,
      },
      {
        name: 'Wheel Balancer',
        type: 'Tools & Equipment',
        icon: faCogs as IconDefinition,
      },
      // Telematics devices
      {
        name: 'GPS Tracker',
        type: 'Telematics devices',
        icon: faSatelliteDish as IconDefinition,
      },
      {
        name: 'Dashcam',
        type: 'Telematics devices',
        icon: faDesktop as IconDefinition,
      },
      {
        name: 'ELD Device',
        type: 'Telematics devices',
        icon: faSatelliteDish as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Daily Vehicle Check',
        defaultFreq: 'daily',
        description: 'Pre-trip inspection',
      },
      {
        name: 'Oil Change',
        defaultFreq: 'monthly',
        description: 'Change engine oil',
      },
      {
        name: 'Tyre Rotation',
        defaultFreq: 'monthly',
        description: 'Rotate and check tyres',
      },
      {
        name: 'Brake Inspection',
        defaultFreq: 'monthly',
        description: 'Check brakes',
      },
      {
        name: 'Trailer Service',
        defaultFreq: 'monthly',
        description: 'Service trailer',
      },
      {
        name: 'Full Service',
        defaultFreq: 'monthly',
        description: 'Complete vehicle service',
      },
      {
        name: 'Registration Renewal',
        defaultFreq: 'monthly',
        description: 'Renew rego',
      },
      {
        name: 'Safety Inspection',
        defaultFreq: 'weekly',
        description: 'Weekly safety check',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // FOOD & BEVERAGE
  // ---------------------------------------------------------------------------
  'Food & Beverage': {
    spaces: [
      { name: 'Kitchen', icon: faFireBurner as IconDefinition },
      { name: 'Prep Area', icon: faKitchenSet as IconDefinition },
      { name: 'Walk-in Cooler', icon: faSnowflake as IconDefinition },
      { name: 'Dining Area', icon: faPlateUtensils as IconDefinition },
      { name: 'Bar', icon: faBeerMugEmpty as IconDefinition },
      { name: 'Storage Room', icon: faBoxes as IconDefinition },
      { name: 'Wash Area', icon: faSink as IconDefinition },
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Loading Dock', icon: faTruck as IconDefinition },
      { name: 'Outdoor Seating', icon: faUmbrellaBeach as IconDefinition },
    ],
    assets: [
      // Cooking equipment
      {
        name: 'Commercial Oven',
        type: 'Cooking equipment',
        icon: faFireBurner as IconDefinition,
      },
      {
        name: 'Stovetop / Range',
        type: 'Cooking equipment',
        icon: faFireBurner as IconDefinition,
      },
      {
        name: 'Deep Fryer',
        type: 'Cooking equipment',
        icon: faFireBurner as IconDefinition,
      },
      {
        name: 'Grill',
        type: 'Cooking equipment',
        icon: faFireBurner as IconDefinition,
      },
      // Refrigeration & cold storage
      {
        name: 'Walk-in Fridge',
        type: 'Refrigeration & cold storage',
        icon: faSnowflake as IconDefinition,
      },
      {
        name: 'Walk-in Freezer',
        type: 'Refrigeration & cold storage',
        icon: faSnowflake as IconDefinition,
      },
      {
        name: 'Display Fridge',
        type: 'Refrigeration & cold storage',
        icon: faSnowflake as IconDefinition,
      },
      // Ventilation & exhaust hoods
      {
        name: 'Exhaust Hood',
        type: 'Ventilation & exhaust hoods',
        icon: faFan as IconDefinition,
      },
      {
        name: 'Ventilation System',
        type: 'Ventilation & exhaust hoods',
        icon: faFan as IconDefinition,
      },
      // Food preparation equipment
      {
        name: 'Commercial Mixer',
        type: 'Food preparation equipment',
        icon: faKitchenSet as IconDefinition,
      },
      {
        name: 'Food Processor',
        type: 'Food preparation equipment',
        icon: faKitchenSet as IconDefinition,
      },
      // Beverage dispensing systems
      {
        name: 'Coffee Machine',
        type: 'Beverage dispensing systems',
        icon: faMugHot as IconDefinition,
      },
      {
        name: 'Beer Tap System',
        type: 'Beverage dispensing systems',
        icon: faBeerMugEmpty as IconDefinition,
      },
      {
        name: 'Soda Fountain',
        type: 'Beverage dispensing systems',
        icon: faGlassWater as IconDefinition,
      },
      // Cleaning & sanitation equipment
      {
        name: 'Commercial Dishwasher',
        type: 'Cleaning & sanitation equipment',
        icon: faPumpSoap as IconDefinition,
      },
      {
        name: 'Sanitisation Station',
        type: 'Cleaning & sanitation equipment',
        icon: faPumpSoap as IconDefinition,
      },
      // Air conditioning units
      {
        name: 'Air Conditioner',
        type: 'Air conditioning units',
        icon: faFan as IconDefinition,
      },
      // Safety & fire systems
      {
        name: 'Fire Suppression System',
        type: 'Safety & fire systems',
        icon: faFire as IconDefinition,
      },
      {
        name: 'Fire Extinguisher',
        type: 'Safety & fire systems',
        icon: faShieldAlt as IconDefinition,
      },
      // First aid equipment
      {
        name: 'First Aid Kit',
        type: 'First aid equipment',
        icon: faShieldAlt as IconDefinition,
      },
      // Grease traps
      {
        name: 'Grease Trap',
        type: 'Grease traps',
        icon: faWater as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Exhaust Hood Clean',
        defaultFreq: 'weekly',
        description: 'Clean exhaust hoods and filters',
        checklist: [
          'Remove and soak grease filters',
          'Wipe down hood interior and exterior',
          'Check fan and motor operation',
          'Clean grease collection tray',
          'Reassemble filters and confirm airflow',
        ],
      },
      {
        name: 'Grease Trap Service',
        defaultFreq: 'monthly',
        description: 'Clean and service grease traps',
        checklist: [
          'Remove lid and inspect trap condition',
          'Scoop out accumulated grease and solids',
          'Scrub baffles, walls, and lid',
          'Check inlet and outlet flow',
          'Replace lid and record service date',
        ],
      },
      {
        name: 'Walk-in Cooler Check',
        defaultFreq: 'daily',
        description: 'Check temperature and seals',
        checklist: [
          'Record internal temperature (should be 0–5 °C)',
          'Check door seal for damage or gaps',
          'Inspect for ice build-up on evaporator coils',
          'Ensure shelves are clean and organised',
          'Verify drain is clear and not blocked',
          'Check that fan is running normally',
        ],
      },
      {
        name: 'Deep Clean',
        defaultFreq: 'weekly',
        description: 'Thorough kitchen deep clean',
        checklist: [
          'Degrease cooking surfaces, splashbacks, and range tops',
          'Clean inside ovens and grills',
          'Wipe down all prep benches and cutting boards',
          'Scrub floors, drains, and floor traps',
          'Clean and sanitise sinks and taps',
          'Empty and wipe out bins and bin areas',
          'Wipe shelving and storage racks',
          'Sanitise door handles, light switches, and high-touch surfaces',
        ],
      },
      {
        name: 'Equipment Service',
        defaultFreq: 'monthly',
        description: 'Service cooking and prep equipment',
        checklist: [
          'Inspect gas connections and pilot lights',
          'Check thermostat accuracy',
          'Lubricate hinges and moving parts',
          'Clean condenser coils on refrigeration units',
          'Test safety cut-off switches',
          'Record any faults or parts needed',
        ],
      },
      {
        name: 'Fire Safety Check',
        defaultFreq: 'monthly',
        description: 'Test fire suppression and extinguishers',
        checklist: [
          'Check fire extinguisher pressure gauge',
          'Inspect fire blanket condition and accessibility',
          'Test fire suppression system indicator lights',
          'Verify emergency exits are clear and signage is visible',
          'Confirm first aid kit is stocked',
        ],
      },
      {
        name: 'Pest Inspection',
        defaultFreq: 'monthly',
        description: 'Inspect for pests and hygiene compliance',
        checklist: [
          'Check behind and under equipment for droppings or nesting',
          'Inspect storage areas and dry goods for signs of pests',
          'Verify bait stations are in place and intact',
          'Check door seals and screens for gaps',
          'Ensure bins are sealed and waste area is clean',
        ],
      },
      {
        name: 'Beverage System Clean',
        defaultFreq: 'weekly',
        description: 'Clean beer lines and coffee machines',
        checklist: [
          'Flush beer lines with cleaning solution',
          'Backflush and descale coffee machine',
          'Clean drip trays and tap heads',
          'Wipe down and sanitise dispensing nozzles',
          'Check CO2 levels and gas connections',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // OTHER INDUSTRY
  // ---------------------------------------------------------------------------
  'Other Industry': {
    spaces: [
      { name: 'Office', icon: faDesktop as IconDefinition },
      { name: 'Workshop', icon: faToolbox as IconDefinition },
      { name: 'Storage', icon: faBoxes as IconDefinition },
      { name: 'Kitchen', icon: faUtensils as IconDefinition },
      { name: 'Meeting Room', icon: faBuilding as IconDefinition },
      { name: 'Reception', icon: faDoorOpen as IconDefinition },
      { name: 'Back Room', icon: faWarehouse as IconDefinition },
      { name: 'Bathroom', icon: faShower as IconDefinition },
    ],
    assets: [
      // Tools
      { name: 'Tool Set', type: 'Tools', icon: faWrench as IconDefinition },
      // Equipment
      { name: 'Printer', type: 'Equipment', icon: faDesktop as IconDefinition },
      {
        name: 'Projector',
        type: 'Equipment',
        icon: faDesktop as IconDefinition,
      },
      // Appliances
      {
        name: 'Coffee Machine',
        type: 'Appliances',
        icon: faUtensils as IconDefinition,
      },
      {
        name: 'Refrigerator',
        type: 'Appliances',
        icon: faBlender as IconDefinition,
      },
      {
        name: 'Microwave',
        type: 'Appliances',
        icon: faUtensils as IconDefinition,
      },
      {
        name: 'Air Conditioner',
        type: 'Appliances',
        icon: faFan as IconDefinition,
      },
      // Vehicles
      { name: 'Company Car', type: 'Vehicles', icon: faCar as IconDefinition },
      // IT equipment
      {
        name: 'Server',
        type: 'IT equipment',
        icon: faServer as IconDefinition,
      },
      {
        name: 'Network Switch',
        type: 'IT equipment',
        icon: faServer as IconDefinition,
      },
      {
        name: 'WiFi Router',
        type: 'IT equipment',
        icon: faServer as IconDefinition,
      },
    ],
    jobs: [
      {
        name: 'Equipment Service',
        defaultFreq: 'monthly',
        description: 'Service equipment',
      },
      {
        name: 'Vehicle Service',
        defaultFreq: 'monthly',
        description: 'Service vehicle',
      },
      {
        name: 'IT Maintenance',
        defaultFreq: 'monthly',
        description: 'IT checkup',
      },
      {
        name: 'General Inspection',
        defaultFreq: 'weekly',
        description: 'Weekly check',
      },
    ],
  },
};

// =============================================================================
// OFFICE-SPECIFIC CONFIGURATION
// =============================================================================

/**
 * Office-specific onboarding config.
 * Used when place type is "Office" or "Office Building" regardless of industry.
 * Provides relevant spaces and assets for a typical office environment.
 */
export const OFFICE_PRESET: IndustryOnboardingConfig = {
  spaces: [
    { name: 'Reception', icon: faDoorOpen as IconDefinition },
    { name: 'Meeting Room', icon: faBuilding as IconDefinition },
    { name: 'Open Plan', icon: faDesktop as IconDefinition },
    { name: 'Kitchen', icon: faUtensils as IconDefinition },
    { name: 'Bathroom', icon: faShower as IconDefinition },
    { name: 'Server Room', icon: faServer as IconDefinition },
    { name: 'Storage', icon: faBoxes as IconDefinition },
    { name: 'Executive Office', icon: faBuildingUser as IconDefinition },
    { name: 'Break Room', icon: faCouch as IconDefinition },
  ],
  assets: [
    // IT equipment
    {
      name: 'Server',
      type: 'Internet / IT equipment',
      icon: faServer as IconDefinition,
    },
    {
      name: 'Network Switch',
      type: 'Internet / IT equipment',
      icon: faServer as IconDefinition,
    },
    {
      name: 'WiFi Router',
      type: 'Internet / IT equipment',
      icon: faServer as IconDefinition,
    },
    {
      name: 'Printer',
      type: 'Internet / IT equipment',
      icon: faDesktop as IconDefinition,
    },
    {
      name: 'Projector',
      type: 'Internet / IT equipment',
      icon: faDesktop as IconDefinition,
    },
    // HVAC units
    {
      name: 'Air Conditioner',
      type: 'HVAC units',
      icon: faFan as IconDefinition,
    },
    {
      name: 'Ventilation System',
      type: 'HVAC units',
      icon: faFan as IconDefinition,
    },
    // Appliances
    {
      name: 'Coffee Machine',
      type: 'Appliances',
      icon: faUtensils as IconDefinition,
    },
    {
      name: 'Refrigerator',
      type: 'Appliances',
      icon: faBlender as IconDefinition,
    },
    {
      name: 'Microwave',
      type: 'Appliances',
      icon: faUtensils as IconDefinition,
    },
    {
      name: 'Dishwasher',
      type: 'Appliances',
      icon: faBlender as IconDefinition,
    },
    // Fire safety equipment
    {
      name: 'Fire Extinguisher',
      type: 'Fire safety equipment',
      icon: faFire as IconDefinition,
    },
    {
      name: 'Smoke Alarm',
      type: 'Smoke alarms',
      icon: faFire as IconDefinition,
    },
    // Doors & Access systems
    {
      name: 'Access Control',
      type: 'Doors & Access systems',
      icon: faDoorOpen as IconDefinition,
    },
  ],
  // Use generic jobs that apply to any selected assets
  jobs: [
    {
      name: 'Inspection',
      defaultFreq: 'monthly',
      description: 'Regular inspection to check condition and function',
    },
    {
      name: 'Service',
      defaultFreq: 'monthly',
      description: 'Routine service and maintenance',
    },
    {
      name: 'Clean',
      defaultFreq: 'weekly',
      description: 'Cleaning and upkeep',
    },
    {
      name: 'Safety Check',
      defaultFreq: 'monthly',
      description: 'Safety and compliance check',
    },
    {
      name: 'Filter Change',
      defaultFreq: 'monthly',
      description: 'Replace filters (where applicable)',
    },
    {
      name: 'Calibration',
      defaultFreq: 'monthly',
      description: 'Calibrate and adjust settings',
    },
  ],
};

/**
 * Check if a place type should use office presets.
 * Matches "Office", "Office Building", etc.
 */
export const isOfficePlaceType = (placeType: string | null): boolean => {
  if (!placeType) return false;
  const normalizedType = placeType.toLowerCase().trim();
  return normalizedType === 'office' || normalizedType === 'office building';
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get onboarding config for a specific industry
 */
export const getOnboardingConfig = (
  industry: string
): IndustryOnboardingConfig | null => {
  return ONBOARDING_PRESETS[industry as IndustryName] ?? null;
};

/**
 * Get space suggestions for an industry
 */
export const getSpaceSuggestions = (industry: string): SpaceSuggestion[] => {
  return ONBOARDING_PRESETS[industry as IndustryName]?.spaces ?? [];
};

/**
 * Get asset suggestions for an industry
 */
export const getAssetSuggestions = (industry: string): AssetSuggestion[] => {
  return ONBOARDING_PRESETS[industry as IndustryName]?.assets ?? [];
};

/**
 * Get job suggestions for an industry
 */
export const getJobSuggestions = (industry: string): JobSuggestion[] => {
  return ONBOARDING_PRESETS[industry as IndustryName]?.jobs ?? [];
};

// =============================================================================
// PLACE TYPE-AWARE HELPER FUNCTIONS
// =============================================================================

/**
 * Get space suggestions based on place type and industry.
 * If place type is "Office" (or "Office Building"), returns office-specific suggestions.
 * If place type is "Other", returns both industry and office suggestions.
 * Otherwise returns industry-based suggestions.
 *
 * @param industry - The selected industry
 * @param placeType - The selected place type (e.g., "Office", "House", "Warehouse", "Other")
 * @returns Array of space suggestions appropriate for the place type
 */
export const getSpaceSuggestionsForPlaceType = (
  industry: string,
  placeType: string | null
): SpaceSuggestion[] => {
  if (isOfficePlaceType(placeType)) {
    return OFFICE_PRESET.spaces;
  }
  if (placeType === 'Other') {
    // Combine industry and office suggestions for "Other" type
    const industrySpaces = getSpaceSuggestions(industry);
    const officeSpaces = OFFICE_PRESET.spaces;
    // Remove duplicates and return combined list
    const combined = [...industrySpaces];
    officeSpaces.forEach((space) => {
      if (!combined.some((s) => s.name === space.name)) {
        combined.push(space);
      }
    });
    return combined;
  }
  return getSpaceSuggestions(industry);
};

/**
 * Get asset suggestions based on place type and industry.
 * If place type is "Office" (or "Office Building"), returns office-specific suggestions.
 * If place type is "Other", returns both industry and office suggestions.
 * Otherwise returns industry-based suggestions.
 *
 * @param industry - The selected industry
 * @param placeType - The selected place type (e.g., "Office", "House", "Warehouse", "Other")
 * @returns Array of asset suggestions appropriate for the place type
 */
export const getAssetSuggestionsForPlaceType = (
  industry: string,
  placeType: string | null
): AssetSuggestion[] => {
  if (isOfficePlaceType(placeType)) {
    return OFFICE_PRESET.assets;
  }
  if (placeType === 'Other') {
    // Combine industry and office suggestions for "Other" type
    const industryAssets = getAssetSuggestions(industry);
    const officeAssets = OFFICE_PRESET.assets;
    // Remove duplicates based on name and return combined list
    const combined = [...industryAssets];
    officeAssets.forEach((asset) => {
      if (!combined.some((a) => a.name === asset.name)) {
        combined.push(asset);
      }
    });
    return combined;
  }
  return getAssetSuggestions(industry);
};

/**
 * Get job suggestions based on place type and industry.
 * If place type is "Office" (or "Office Building"), returns office-specific suggestions.
 * If place type is "Other", returns both industry and office suggestions.
 * Otherwise returns industry-based suggestions.
 *
 * @param industry - The selected industry
 * @param placeType - The selected place type (e.g., "Office", "House", "Warehouse", "Other")
 * @returns Array of job suggestions appropriate for the place type
 */
export const getJobSuggestionsForPlaceType = (
  industry: string,
  placeType: string | null
): JobSuggestion[] => {
  if (isOfficePlaceType(placeType)) {
    return OFFICE_PRESET.jobs;
  }
  if (placeType === 'Other') {
    // Combine industry and office suggestions for "Other" type
    const industryJobs = getJobSuggestions(industry);
    const officeJobs = OFFICE_PRESET.jobs;
    // Remove duplicates based on name and return combined list
    const combined = [...industryJobs];
    officeJobs.forEach((job) => {
      if (!combined.some((j) => j.name === job.name)) {
        combined.push(job);
      }
    });
    return combined;
  }
  return getJobSuggestions(industry);
};

/**
 * Get full onboarding config based on place type and industry.
 * If place type is "Office" (or "Office Building"), returns office-specific config.
 * If place type is "Other", returns combined industry and office config.
 * Otherwise returns industry-based config.
 *
 * @param industry - The selected industry
 * @param placeType - The selected place type (e.g., "Office", "House", "Warehouse", "Other")
 * @returns Full onboarding configuration for the place type
 */
export const getOnboardingConfigForPlaceType = (
  industry: string,
  placeType: string | null
): IndustryOnboardingConfig | null => {
  if (isOfficePlaceType(placeType)) {
    return OFFICE_PRESET;
  }
  if (placeType === 'Other') {
    // Return combined config for "Other" type
    const industryConfig = getOnboardingConfig(industry);
    if (!industryConfig) return null;

    return {
      spaces: getSpaceSuggestionsForPlaceType(industry, 'Other'),
      assets: getAssetSuggestionsForPlaceType(industry, 'Other'),
      jobs: getJobSuggestionsForPlaceType(industry, 'Other'),
    };
  }
  return getOnboardingConfig(industry);
};

// =============================================================================
// GENERIC JOB SUGGESTIONS (for multi-asset onboarding)
// =============================================================================

/**
 * Generic job types that apply to virtually any asset.
 * These are used when multiple assets are selected during onboarding
 * to provide suggestions that make sense regardless of asset type.
 */
export const GENERIC_JOB_SUGGESTIONS: JobSuggestion[] = [
  {
    name: 'Inspection',
    defaultFreq: 'monthly',
    description: 'Regular inspection to check condition and function',
  },
  {
    name: 'Service',
    defaultFreq: 'monthly',
    description: 'Routine service and maintenance',
  },
  {
    name: 'Clean',
    defaultFreq: 'weekly',
    description: 'Cleaning and upkeep',
  },
  {
    name: 'Safety Check',
    defaultFreq: 'monthly',
    description: 'Safety and compliance check',
  },
  {
    name: 'Filter Change',
    defaultFreq: 'monthly',
    description: 'Replace filters (where applicable)',
  },
  {
    name: 'Calibration',
    defaultFreq: 'monthly',
    description: 'Calibrate and adjust settings',
  },
];

// =============================================================================
// FOOD & BEVERAGE — ASSET-SPECIFIC JOB MAPPING
// =============================================================================

/**
 * Maps F&B asset types (and specific asset names) to the jobs that actually
 * make sense for that equipment.  Used by getSmartJobSuggestions when the
 * industry is "Food & Beverage" so we stop suggesting things like
 * "Deep Clean" for a Grill or "Filter Change" for a Grease Trap.
 *
 * Key precedence:
 *   1. Exact asset-name match  (e.g. "Grill", "Coffee Machine")
 *   2. Asset-type match        (e.g. "Cooking equipment")
 *   3. Fall-through to base F&B generic list
 */

type FBJobMapping = {
  jobs: JobSuggestion[];
};

/**
 * Jobs keyed by F&B asset TYPE (maps to the `type` field on AssetSuggestion).
 */
export const FB_ASSET_TYPE_JOBS: Record<string, FBJobMapping> = {
  'Cooking equipment': {
    jobs: [
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Routine service and maintenance',
      },
      {
        name: 'Clean',
        defaultFreq: 'daily',
        description: 'Wipe down and degrease after service',
      },
      {
        name: 'Safety Check',
        defaultFreq: 'monthly',
        description: 'Check gas connections and safety cut-offs',
      },
    ],
  },
  'Refrigeration & cold storage': {
    jobs: [
      {
        name: 'Temperature Log',
        defaultFreq: 'daily',
        description: 'Record fridge / freezer temperatures',
        checklist: [
          'Record internal temperature (fridge 0–5 °C, freezer ≤ −18 °C)',
          'Check door seal for damage or gaps',
          'Inspect for ice build-up on coils',
          'Verify drain is clear',
        ],
      },
      {
        name: 'Condenser Clean',
        defaultFreq: 'monthly',
        description: 'Clean condenser coils to maintain efficiency',
        checklist: [
          'Power off the unit',
          'Vacuum dust and debris from condenser coils',
          'Wipe housing and fan blades',
          'Power on and confirm normal operation',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect seals, shelves and refrigerant levels',
      },
    ],
  },
  'Ventilation & exhaust hoods': {
    jobs: [
      {
        name: 'Exhaust Hood Clean',
        defaultFreq: 'weekly',
        description: 'Clean exhaust hoods and filters',
        checklist: [
          'Remove and soak grease filters',
          'Wipe down hood interior and exterior',
          'Check fan and motor operation',
          'Clean grease collection tray',
          'Reassemble filters and confirm airflow',
        ],
      },
      {
        name: 'Filter Change',
        defaultFreq: 'monthly',
        description: 'Replace exhaust hood filters',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect fan motor, belt and ductwork',
      },
    ],
  },
  'Food preparation equipment': {
    jobs: [
      {
        name: 'Clean',
        defaultFreq: 'daily',
        description: 'Disassemble, wash and sanitise after use',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Check blades, seals and moving parts',
      },
    ],
  },
  'Beverage dispensing systems': {
    jobs: [
      {
        name: 'Line Clean',
        defaultFreq: 'weekly',
        description: 'Flush and clean beverage lines',
        checklist: [
          'Flush lines with cleaning solution',
          'Clean drip trays and tap heads',
          'Sanitise dispensing nozzles',
          'Check CO₂ levels and gas connections',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Service pumps, seals and regulators',
      },
    ],
  },
  'Cleaning & sanitation equipment': {
    jobs: [
      {
        name: 'Clean',
        defaultFreq: 'daily',
        description: 'Clean filters, spray arms and interior',
      },
      {
        name: 'Descale',
        defaultFreq: 'monthly',
        description: 'Descale dishwasher or sanitiser',
        checklist: [
          'Run descaling cycle with approved chemical',
          'Clean spray arms and filters',
          'Wipe door seals and interior walls',
          'Run a rinse cycle and test water temperature',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect heating element, pump and drainage',
      },
    ],
  },
  'Air conditioning units': {
    jobs: [
      {
        name: 'Filter Change',
        defaultFreq: 'monthly',
        description: 'Replace air-conditioning filters',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Service compressor and check refrigerant',
      },
    ],
  },
  'Safety & fire systems': {
    jobs: [
      {
        name: 'Fire Safety Check',
        defaultFreq: 'monthly',
        description: 'Test fire suppression and extinguishers',
        checklist: [
          'Check fire extinguisher pressure gauge',
          'Inspect fire blanket condition and accessibility',
          'Test fire suppression system indicator lights',
          'Verify emergency exits are clear and signage visible',
        ],
      },
    ],
  },
  'First aid equipment': {
    jobs: [
      {
        name: 'Restock Check',
        defaultFreq: 'monthly',
        description: 'Check and restock first aid supplies',
        checklist: [
          'Verify all items are in-date',
          'Replace used or missing supplies',
          'Ensure kit is sealed and accessible',
        ],
      },
    ],
  },
  'Grease traps': {
    jobs: [
      {
        name: 'Grease Trap Service',
        defaultFreq: 'monthly',
        description: 'Clean and service grease traps',
        checklist: [
          'Remove lid and inspect trap condition',
          'Scoop out accumulated grease and solids',
          'Scrub baffles, walls and lid',
          'Check inlet and outlet flow',
          'Replace lid and record service date',
        ],
      },
    ],
  },
};

/**
 * Jobs keyed by exact F&B asset NAME — overrides the type-level mapping.
 * Use this for assets where the type-level jobs are too broad.
 */
export const FB_ASSET_NAME_JOBS: Record<string, FBJobMapping> = {
  Grill: {
    jobs: [
      {
        name: 'Grill Clean',
        defaultFreq: 'daily',
        description: 'Scrape, brush and wipe down the grill',
        checklist: [
          'Scrape grill grates with a wire brush while warm',
          'Wipe down exterior surfaces',
          'Empty and clean grease drip tray',
          'Check gas burners for blockages',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect burners, igniters and gas connections',
      },
    ],
  },
  'Deep Fryer': {
    jobs: [
      {
        name: 'Oil Change',
        defaultFreq: 'weekly',
        description: 'Drain, filter or replace fryer oil',
        checklist: [
          'Power off and allow oil to cool to safe temperature',
          'Drain oil into approved container',
          'Scrub interior basket and tank',
          'Refill with fresh oil and test temperature',
        ],
      },
      {
        name: 'Boil Out',
        defaultFreq: 'monthly',
        description: 'Deep clean fryer with boil-out solution',
        checklist: [
          'Drain all oil and remove baskets',
          'Fill with water and boil-out chemical',
          'Boil for recommended duration',
          'Drain, rinse thoroughly and dry',
          'Refill with fresh oil',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Check thermostat, heating element and safety cut-off',
      },
    ],
  },
  'Commercial Oven': {
    jobs: [
      {
        name: 'Oven Clean',
        defaultFreq: 'weekly',
        description: 'Deep clean oven interior and racks',
        checklist: [
          'Remove racks and soak in degreaser',
          'Spray interior with oven cleaner',
          'Scrub interior walls, floor and door glass',
          'Wipe door seals and check for damage',
          'Replace racks and test thermostat',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect gas connections, thermostat and door seals',
      },
    ],
  },
  'Stovetop / Range': {
    jobs: [
      {
        name: 'Clean',
        defaultFreq: 'daily',
        description: 'Degrease burners and wipe surfaces',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Check burners, igniters and gas connections',
      },
    ],
  },
  'Coffee Machine': {
    jobs: [
      {
        name: 'Backflush',
        defaultFreq: 'daily',
        description: 'Backflush group heads with cleaning powder',
        checklist: [
          'Insert blind filter with cleaning powder',
          'Run backflush cycle on each group head',
          'Rinse portafilters and baskets',
          'Wipe steam wands and drip tray',
        ],
      },
      {
        name: 'Descale',
        defaultFreq: 'monthly',
        description: 'Descale boiler and internal components',
        checklist: [
          'Run descaling solution through the machine',
          'Flush with clean water until residue is clear',
          'Clean and reassemble water filter',
          'Test shot quality and steam pressure',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect seals, group heads and grinder burrs',
      },
    ],
  },
  'Beer Tap System': {
    jobs: [
      {
        name: 'Line Clean',
        defaultFreq: 'weekly',
        description: 'Flush beer lines with cleaning solution',
        checklist: [
          'Disconnect kegs and flush lines with cleaner',
          'Soak tap heads and faucets',
          'Rinse lines with fresh water',
          'Reconnect kegs and pour test beer',
          'Check CO₂ regulator pressure',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Service regulators, couplers and glycol chiller',
      },
    ],
  },
  'Soda Fountain': {
    jobs: [
      {
        name: 'Nozzle Clean',
        defaultFreq: 'daily',
        description: 'Remove and soak dispensing nozzles',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect syrup lines, carbonator and CO₂ supply',
      },
    ],
  },
  'Exhaust Hood': {
    jobs: [
      {
        name: 'Exhaust Hood Clean',
        defaultFreq: 'weekly',
        description: 'Clean exhaust hoods and filters',
        checklist: [
          'Remove and soak grease filters',
          'Wipe down hood interior and exterior',
          'Check fan and motor operation',
          'Clean grease collection tray',
          'Reassemble filters and confirm airflow',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect fan motor, belt and ductwork',
      },
    ],
  },
  'Grease Trap': {
    jobs: [
      {
        name: 'Grease Trap Service',
        defaultFreq: 'monthly',
        description: 'Clean and service grease traps',
        checklist: [
          'Remove lid and inspect trap condition',
          'Scoop out accumulated grease and solids',
          'Scrub baffles, walls and lid',
          'Check inlet and outlet flow',
          'Replace lid and record service date',
        ],
      },
    ],
  },
  'Commercial Dishwasher': {
    jobs: [
      {
        name: 'Clean',
        defaultFreq: 'daily',
        description: 'Clean filters, spray arms and interior',
      },
      {
        name: 'Descale',
        defaultFreq: 'monthly',
        description: 'Descale dishwasher with approved chemical',
        checklist: [
          'Run descaling cycle',
          'Clean spray arms and filters',
          'Wipe door seals and interior walls',
          'Run rinse cycle and test water temperature',
        ],
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Inspect heating element, pump and drainage',
      },
    ],
  },
  'Fire Suppression System': {
    jobs: [
      {
        name: 'Fire Safety Check',
        defaultFreq: 'monthly',
        description: 'Test suppression system and indicator lights',
        checklist: [
          'Test fire suppression system indicator lights',
          'Verify nozzles are unobstructed',
          'Check manual pull station',
          'Verify emergency exits are clear and signage visible',
        ],
      },
    ],
  },
  'Fire Extinguisher': {
    jobs: [
      {
        name: 'Fire Safety Check',
        defaultFreq: 'monthly',
        description: 'Inspect extinguisher pressure and accessibility',
        checklist: [
          'Check pressure gauge is in green zone',
          'Inspect pin, tamper seal and hose',
          'Verify signage and mounting bracket',
          'Confirm service tag is in-date',
        ],
      },
    ],
  },
};

/**
 * Get smart job suggestions based on selected assets.
 *
 * For Food & Beverage the function uses the granular FB_ASSET_NAME_JOBS and
 * FB_ASSET_TYPE_JOBS mappings so each piece of equipment gets contextually
 * relevant jobs (e.g. a Grill gets "Grill Clean", not "Deep Clean").
 *
 * For all other industries the original generic + type-based approach is used.
 *
 * @param assetNames - Array of selected asset names
 * @param assetTypes - Optional array of asset types for more context
 * @param industry - Optional industry name to filter out industry-inappropriate jobs
 * @returns Array of job suggestions appropriate for the assets
 */
export const getSmartJobSuggestions = (
  assetNames: string[],
  assetTypes?: string[],
  industry?: string
): JobSuggestion[] => {
  // -----------------------------------------------------------------
  // FOOD & BEVERAGE — asset-specific path
  // -----------------------------------------------------------------
  if (industry === 'Food & Beverage') {
    return getFBSmartJobSuggestions(assetNames, assetTypes);
  }

  // -----------------------------------------------------------------
  // ALL OTHER INDUSTRIES — generic path (unchanged behaviour)
  // -----------------------------------------------------------------
  let suggestions: JobSuggestion[] = [...GENERIC_JOB_SUGGESTIONS];

  // Filter out Calibration and Filter Change for property managers
  // These jobs are typically not relevant for property management workflows
  if (industry === 'Holiday Rentals & Property Management') {
    suggestions = suggestions.filter(
      (job) => job.name !== 'Calibration' && job.name !== 'Filter Change'
    );
  }

  // If we have asset types, we can add some type-specific suggestions
  const types = new Set(assetTypes || []);

  // Add vehicle-specific jobs
  if (
    types.has('Vehicles') ||
    types.has('Trailers') ||
    assetNames.some((name) =>
      /truck|van|car|vehicle|ute|bus|trailer/i.test(name)
    )
  ) {
    suggestions.push({
      name: 'Oil Change',
      defaultFreq: 'monthly',
      description: 'Change engine oil and filter',
    });
    suggestions.push({
      name: 'Tyre Check',
      defaultFreq: 'weekly',
      description: 'Check tyre pressure and condition',
    });
  }

  // Add appliance-specific jobs
  if (
    types.has('Appliances') ||
    types.has('HVAC units') ||
    assetNames.some((name) =>
      /washer|dryer|fridge|refrigerator|oven|microwave|dishwasher|air con/i.test(
        name
      )
    )
  ) {
    suggestions.push({
      name: 'Deep Clean',
      defaultFreq: 'monthly',
      description: 'Thorough internal cleaning',
    });
  }

  // Add pool/spa specific jobs
  if (
    types.has('Pools & spas') ||
    assetNames.some((name) => /pool|spa|pump/i.test(name))
  ) {
    suggestions.push({
      name: 'Chemical Balance',
      defaultFreq: 'weekly',
      description: 'Test and balance water chemistry',
    });
  }

  // Add safety equipment jobs
  if (
    types.has('Fire safety equipment') ||
    types.has('Smoke alarms') ||
    assetNames.some((name) => /smoke|fire|extinguisher|alarm/i.test(name))
  ) {
    suggestions.push({
      name: 'Test Alarm',
      defaultFreq: 'monthly',
      description: 'Test alarm functionality',
    });
  }

  // Add refrigeration-specific jobs
  if (
    types.has('Refrigeration & cold storage') ||
    assetNames.some((name) =>
      /fridge|freezer|cooler|cold storage|refrigerat/i.test(name)
    )
  ) {
    suggestions.push({
      name: 'Temperature Log',
      defaultFreq: 'daily',
      description: 'Record fridge and freezer temperatures',
    });
  }

  // Add cooking equipment-specific jobs
  if (
    types.has('Cooking equipment') ||
    assetNames.some((name) =>
      /oven|stovetop|range|fryer|grill|griddle/i.test(name)
    )
  ) {
    suggestions.push({
      name: 'Deep Clean',
      defaultFreq: 'weekly',
      description: 'Deep clean cooking equipment',
    });
  }

  // Add grease trap jobs
  if (
    types.has('Grease traps') ||
    assetNames.some((name) => /grease trap/i.test(name))
  ) {
    suggestions.push({
      name: 'Grease Trap Service',
      defaultFreq: 'monthly',
      description: 'Clean and service grease traps',
    });
  }

  // Add beverage system jobs
  if (
    types.has('Beverage dispensing systems') ||
    assetNames.some((name) =>
      /beer tap|coffee machine|soda fountain|beverage/i.test(name)
    )
  ) {
    suggestions.push({
      name: 'Line Clean',
      defaultFreq: 'weekly',
      description: 'Clean beverage lines and dispensers',
    });
  }

  return suggestions;
};

// =============================================================================
// F&B-SPECIFIC SMART JOB BUILDER
// =============================================================================

/**
 * Build job suggestions specifically for Food & Beverage assets.
 *
 * Resolution order for each asset:
 *   1. Exact name match in FB_ASSET_NAME_JOBS
 *   2. Type match in FB_ASSET_TYPE_JOBS
 *   3. Fall through — asset gets the base F&B generic list
 *
 * The final list is de-duplicated by job name so users never see the same
 * suggestion twice even when multiple selected assets share a job.
 */
const getFBSmartJobSuggestions = (
  assetNames: string[],
  assetTypes?: string[]
): JobSuggestion[] => {
  const seen = new Set<string>();
  const result: JobSuggestion[] = [];

  const addUnique = (job: JobSuggestion): void => {
    if (!seen.has(job.name)) {
      seen.add(job.name);
      result.push(job);
    }
  };

  // Build a name → type lookup for quick reference
  const nameToType = new Map<string, string>();
  if (assetTypes && assetTypes.length === assetNames.length) {
    assetNames.forEach((name, i) => nameToType.set(name, assetTypes[i]));
  }

  // Track whether any asset was resolved via name or type mapping
  let anyMapped = false;

  for (const assetName of assetNames) {
    // 1. Exact name match
    const nameMapping = FB_ASSET_NAME_JOBS[assetName];
    if (nameMapping) {
      nameMapping.jobs.forEach(addUnique);
      anyMapped = true;
      continue;
    }

    // 2. Type match
    const assetType = nameToType.get(assetName);
    if (assetType) {
      const typeMapping = FB_ASSET_TYPE_JOBS[assetType];
      if (typeMapping) {
        typeMapping.jobs.forEach(addUnique);
        anyMapped = true;
        continue;
      }
    }

    // 3. No specific mapping — will get base list below
  }

  // If no assets matched any mapping (or list is empty), provide sensible F&B
  // base suggestions so the user always sees something useful
  if (!anyMapped || result.length === 0) {
    const fbBase: JobSuggestion[] = [
      {
        name: 'Inspection',
        defaultFreq: 'monthly',
        description: 'Regular inspection to check condition and function',
      },
      {
        name: 'Service',
        defaultFreq: 'monthly',
        description: 'Routine service and maintenance',
      },
      {
        name: 'Clean',
        defaultFreq: 'weekly',
        description: 'Cleaning and upkeep',
      },
      {
        name: 'Safety Check',
        defaultFreq: 'monthly',
        description: 'Safety and compliance check',
      },
      {
        name: 'Temperature Log',
        defaultFreq: 'daily',
        description: 'Record fridge and freezer temperatures',
      },
    ];
    fbBase.forEach(addUnique);
  }

  return result;
};

/**
 * Custom space icon (for user-added spaces)
 */
export const CUSTOM_SPACE_ICON = faPlus as IconDefinition;

// =============================================================================
// SMART SPACE SUGGESTION SYSTEM
// =============================================================================

/**
 * Parsed representation of a space name with optional suffix.
 * Examples:
 *   "Bedroom 1" → { baseName: "Bedroom", suffix: 1, suffixType: "number", fullName: "Bedroom 1" }
 *   "Zone A" → { baseName: "Zone", suffix: "A", suffixType: "letter", fullName: "Zone A" }
 *   "Kitchen" → { baseName: "Kitchen", suffix: null, suffixType: null, fullName: "Kitchen" }
 */
export type ParsedSpaceName = {
  baseName: string;
  suffix: number | string | null;
  suffixType: 'number' | 'letter' | null;
  fullName: string;
};

/**
 * A dynamically generated space suggestion based on user selections.
 */
export type DynamicSpaceSuggestion = {
  name: string; // Full space name (e.g., "Bedroom 1/Ensuite" or "Bedroom 3")
  displayName: string; // Display on card (e.g., "Ensuite" for sub-spaces, "Bedroom 3" for sequential)
  icon: IconDefinition;
  suggestionType: 'sub-space' | 'sequential';
  triggerSpace: string; // Which selected space triggered this suggestion
  parentPath?: string; // For sub-spaces: "Bedroom 1"
};

/**
 * Configuration for sub-spaces that can be suggested when a parent space is selected.
 * Key: base name of parent space (without number/suffix)
 * Value: array of possible child spaces with their icons
 */
export const SUB_SPACE_CONFIG: Record<
  string,
  { name: string; icon: IconDefinition }[]
> = {
  Bedroom: [{ name: 'Ensuite', icon: faShower as IconDefinition }],
  Kitchen: [{ name: 'Pantry', icon: faBoxes as IconDefinition }],
  Garage: [{ name: 'Workshop', icon: faToolbox as IconDefinition }],
  Office: [{ name: 'Storage', icon: faBoxes as IconDefinition }],
  'Pool Area': [{ name: 'Equipment Room', icon: faCogs as IconDefinition }],
  'Outdoor Area': [{ name: 'BBQ Area', icon: faTree as IconDefinition }],
  'Server Room': [{ name: 'UPS Room', icon: faBolt as IconDefinition }],
  Workshop: [{ name: 'Tool Storage', icon: faWrench as IconDefinition }],
};

/**
 * Default icon for sequential space suggestions
 */
const DEFAULT_SEQUENTIAL_ICON = faBuilding as IconDefinition;

/**
 * Parse a space name to extract base name and suffix (number or letter).
 *
 * @param name - The space name to parse (e.g., "Bedroom 1", "Zone A", "Kitchen")
 * @returns Parsed space name object
 */
export const parseSpaceName = (name: string): ParsedSpaceName => {
  // Match patterns like "Bedroom 1", "Zone A", "Workshop Bay 2"
  // Pattern: any text followed by space and then a number OR single letter
  const numberMatch = name.match(/^(.+?)\s+(\d+)$/);
  if (numberMatch) {
    return {
      baseName: numberMatch[1],
      suffix: parseInt(numberMatch[2], 10),
      suffixType: 'number',
      fullName: name,
    };
  }

  const letterMatch = name.match(/^(.+?)\s+([A-Z])$/i);
  if (letterMatch) {
    return {
      baseName: letterMatch[1],
      suffix: letterMatch[2].toUpperCase(),
      suffixType: 'letter',
      fullName: name,
    };
  }

  // No suffix found
  return {
    baseName: name,
    suffix: null,
    suffixType: null,
    fullName: name,
  };
};

/**
 * Get the next sequential suffix value.
 *
 * @param suffix - Current suffix (number or letter)
 * @param suffixType - Type of suffix
 * @returns Next suffix value
 */
export const getNextSuffix = (
  suffix: number | string,
  suffixType: 'number' | 'letter'
): number | string | null => {
  if (suffixType === 'number' && typeof suffix === 'number') {
    return suffix + 1;
  }
  if (suffixType === 'letter' && typeof suffix === 'string') {
    // A → B, B → C, ..., Z → null (no further suffix)
    const charCode = suffix.toUpperCase().charCodeAt(0);
    if (charCode >= 65 && charCode < 90) {
      // A-Y → next letter
      return String.fromCharCode(charCode + 1);
    }
    // Z → null (no further suffix to avoid duplicates)
    return null;
  }
  // Invalid state: suffix type doesn't match suffix value type
  return null;
};

/**
 * Build a sub-space name with "/" separator.
 *
 * @param parent - Parent space name (e.g., "Bedroom 1")
 * @param child - Child space name (e.g., "Ensuite")
 * @returns Combined name (e.g., "Bedroom 1/Ensuite")
 */
export const buildSubSpaceName = (parent: string, child: string): string => {
  return `${parent}/${child}`;
};

/**
 * Get dynamic space suggestions based on currently selected spaces.
 * This function computes what additional cards should be shown based on user selections.
 *
 * Rules:
 * - If a numbered space is selected (e.g., "Bedroom 2"), suggest the next number ("Bedroom 3")
 *   but ONLY if it's not already in suggestions or selected
 * - If a space has sub-space config (e.g., "Bedroom 1" → "Ensuite"), suggest the sub-space
 *   but ONLY if not already selected
 * - Only show n+1, not n+2, n+3, etc.
 *
 * @param selectedSpaces - Currently selected space names
 * @param customSpaces - User-added custom spaces
 * @param baseSuggestions - Base suggestions from industry config
 * @param suggestionIcons - Map of suggestion names to their icons (from baseSuggestions)
 * @returns Array of dynamic suggestions to display
 */
export const getDynamicSpaceSuggestions = (
  selectedSpaces: string[],
  customSpaces: string[],
  baseSuggestions: SpaceSuggestion[]
): DynamicSpaceSuggestion[] => {
  const suggestions: DynamicSpaceSuggestion[] = [];
  const allExistingNames = new Set([
    ...baseSuggestions.map((s) => s.name),
    ...customSpaces,
    ...selectedSpaces,
  ]);

  // Create a map of suggestion names to icons for quick lookup
  const iconMap = new Map<string, IconDefinition>();
  baseSuggestions.forEach((s) => iconMap.set(s.name, s.icon));

  // Track which suggestions we've already added to avoid duplicates
  const addedSuggestions = new Set<string>();

  // Process each selected space
  for (const spaceName of selectedSpaces) {
    const parsed = parseSpaceName(spaceName);

    // 1. Check for sub-space suggestions
    // Look up by base name first, then by full name for non-numbered spaces
    const subSpaceConfigs =
      SUB_SPACE_CONFIG[parsed.baseName] || SUB_SPACE_CONFIG[spaceName];

    if (subSpaceConfigs) {
      for (const subSpace of subSpaceConfigs) {
        const fullSubSpaceName = buildSubSpaceName(spaceName, subSpace.name);

        // Only suggest if not already existing and not already suggested
        if (
          !allExistingNames.has(fullSubSpaceName) &&
          !addedSuggestions.has(fullSubSpaceName)
        ) {
          suggestions.push({
            name: fullSubSpaceName,
            displayName: subSpace.name,
            icon: subSpace.icon,
            suggestionType: 'sub-space',
            triggerSpace: spaceName,
            parentPath: spaceName,
          });
          addedSuggestions.add(fullSubSpaceName);
        }
      }
    }

    // 2. Check for sequential suggestions (only if space has a suffix)
    if (parsed.suffix !== null && parsed.suffixType !== null) {
      const nextSuffix = getNextSuffix(parsed.suffix, parsed.suffixType);
      if (nextSuffix !== null) {
        const nextSpaceName = `${parsed.baseName} ${nextSuffix}`;

        // Only suggest n+1 if:
        // - It's not already in base suggestions
        // - It's not already selected
        // - It's not a custom space
        // - We haven't already suggested it
        if (
          !allExistingNames.has(nextSpaceName) &&
          !addedSuggestions.has(nextSpaceName)
        ) {
          // Try to get icon from a similar space, or use default
          const similarIcon =
            iconMap.get(`${parsed.baseName} 1`) ||
            iconMap.get(`${parsed.baseName} 2`) ||
            iconMap.get(parsed.baseName) ||
            DEFAULT_SEQUENTIAL_ICON;

          suggestions.push({
            name: nextSpaceName,
            displayName: nextSpaceName,
            icon: similarIcon,
            suggestionType: 'sequential',
            triggerSpace: spaceName,
          });
          addedSuggestions.add(nextSpaceName);
        }
      }
    }
  }

  return suggestions;
};

/**
 * Infer an icon for a custom space name based on pattern matching.
 * Uses the base suggestions and SUB_SPACE_CONFIG to find appropriate icons.
 *
 * @param spaceName - The custom space name (e.g., "Bedroom 3", "Kitchen/Pantry")
 * @param baseSuggestions - Base suggestions from industry config for icon lookup
 * @returns An appropriate icon for the space
 */
export const inferSpaceIcon = (
  spaceName: string,
  baseSuggestions: SpaceSuggestion[]
): IconDefinition => {
  // Create icon map from suggestions
  const iconMap = new Map<string, IconDefinition>();
  baseSuggestions.forEach((s) => iconMap.set(s.name, s.icon));

  // Check if it's a sub-space (contains "/")
  if (spaceName.includes('/')) {
    const [parentName, childName] = spaceName.split('/');
    const parsed = parseSpaceName(parentName);

    // Look for sub-space icon in config
    const subSpaceConfigs =
      SUB_SPACE_CONFIG[parsed.baseName] || SUB_SPACE_CONFIG[parentName];
    if (subSpaceConfigs) {
      const subSpace = subSpaceConfigs.find((s) => s.name === childName);
      if (subSpace) return subSpace.icon;
    }

    // Fall back to parent's icon
    const parentIcon =
      iconMap.get(parentName) ||
      iconMap.get(`${parsed.baseName} 1`) ||
      iconMap.get(parsed.baseName);
    if (parentIcon) return parentIcon;
  }

  // Parse to check for numbered/lettered patterns
  const parsed = parseSpaceName(spaceName);

  // Try to find icon from similar spaces
  if (parsed.baseName) {
    const similarIcon =
      iconMap.get(`${parsed.baseName} 1`) ||
      iconMap.get(`${parsed.baseName} 2`) ||
      iconMap.get(parsed.baseName);
    if (similarIcon) return similarIcon;
  }

  // Check if exact match exists
  const exactIcon = iconMap.get(spaceName);
  if (exactIcon) return exactIcon;

  // Default to generic building icon
  return DEFAULT_SEQUENTIAL_ICON;
};

// =============================================================================
// INDUSTRY OPTIONS (for onboarding flow)
// =============================================================================

export const INDUSTRY_OPTIONS: IndustryName[] = [
  'Food & Beverage',
  'Holiday Rentals & Property Management',
  'Cleaning & Laundry Services',
  'Facilities Maintenance',
  'Trades & Construction',
  'Industrial',
  'Fleet & Transport',

  'Other Industry',
];

// =============================================================================
// INDUSTRY PLACE CONFIGURATION
// =============================================================================

export type IndustryPlaceConfig = {
  suggestions: string[];
  hint: string;
};

export const INDUSTRY_PLACE_CONFIG: Record<IndustryName, IndustryPlaceConfig> =
  {
    'Holiday Rentals & Property Management': {
      suggestions: ['Unit 1', '45 Beach Road', 'The Blue House'],
      hint: "Use your property name or address — e.g. 'Apt 4B', '12 Ocean Drive'",
    },
    'Cleaning & Laundry Services': {
      suggestions: ['Main Depot', 'Downtown Hub', 'Client Site'],
      hint: "Name the location you service — e.g. 'Hilton Brisbane', 'CBD Office Block'",
    },
    'Facilities Maintenance': {
      suggestions: ['Head Office', 'Building A', 'Campus North'],
      hint: "Use building names or codes — e.g. 'Tower 1', 'Warehouse B3'",
    },
    'Trades & Construction': {
      suggestions: ['Workshop', 'Job Site', 'Client Property'],
      hint: "Name your site or project — e.g. 'Smith Renovation', '45 King St Build'",
    },
    Industrial: {
      suggestions: ['Plant 1', 'Factory Floor', 'Distribution Centre'],
      hint: "Use plant or zone names — e.g. 'Assembly Line A', 'Cold Storage'",
    },
    'Fleet & Transport': {
      suggestions: ['Depot', 'Service Bay', 'Truck Yard'],
      hint: "Name your base or route hub — e.g. 'North Depot', 'Airport Lot'",
    },
    'Food & Beverage': {
      suggestions: ['Downtown Cafe', 'The Corner Bar'],
      hint: 'Use your venue name or location',
    },
    'Other Industry': {
      suggestions: ['Main Office', 'Site 1', 'Location A'],
      hint: "Use a nickname or the street — e.g. 'HQ', '12 Smith Street'",
    },
  };

export const DEFAULT_PLACE_CONFIG: IndustryPlaceConfig = {
  suggestions: ['Main Office', 'Villa', 'Short Term Rental'],
  hint: "You can use a nickname or the street — e.g. 'Seaside Apartments', '12 Smith Street'",
};

/**
 * Get place configuration for a specific industry
 */
export const getPlaceConfig = (
  industry: string | null
): IndustryPlaceConfig => {
  if (!industry) return DEFAULT_PLACE_CONFIG;
  return (
    INDUSTRY_PLACE_CONFIG[industry as IndustryName] ?? DEFAULT_PLACE_CONFIG
  );
};

// =============================================================================
// PLACE TYPE CONFIGURATION (for site type selection)
// =============================================================================

export type PlaceTypeSuggestion = {
  name: string;
  icon: IconDefinition;
};

/**
 * Place type suggestions by industry.
 * Office is placed first when available (as per requirements).
 */
export const INDUSTRY_PLACE_TYPES: Record<IndustryName, PlaceTypeSuggestion[]> =
  {
    'Holiday Rentals & Property Management': [
      { name: 'Apartment', icon: faBuilding as IconDefinition },
      { name: 'House', icon: faHouse as IconDefinition },
      { name: 'Villa', icon: faUmbrellaBeach as IconDefinition },
      { name: 'Cabin', icon: faHouseChimneyWindow as IconDefinition },
      { name: 'Unit', icon: faDoorOpen as IconDefinition },
      { name: 'Townhouse', icon: faHome as IconDefinition },
      { name: 'Studio', icon: faBed as IconDefinition },
      { name: 'Hotel', icon: faHotel as IconDefinition },
    ],
    'Cleaning & Laundry Services': [
      { name: 'Office', icon: faBuildingUser as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Depot', icon: faTruck as IconDefinition },
      { name: 'Client Site', icon: faLocationDot as IconDefinition },
      { name: 'Retail Store', icon: faStore as IconDefinition },
      { name: 'Commercial', icon: faBuilding as IconDefinition },
    ],
    'Facilities Maintenance': [
      { name: 'Office Building', icon: faBuildingUser as IconDefinition },
      { name: 'Campus', icon: faBuilding as IconDefinition },
      { name: 'Complex', icon: faBuilding as IconDefinition },
      { name: 'Shopping Centre', icon: faStore as IconDefinition },
      { name: 'Hospital', icon: faShieldAlt as IconDefinition },
      { name: 'School', icon: faBuilding as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
    ],
    'Trades & Construction': [
      { name: 'Office', icon: faBuildingUser as IconDefinition },
      { name: 'Workshop', icon: faToolbox as IconDefinition },
      { name: 'Job Site', icon: faHardHat as IconDefinition },
      { name: 'Yard', icon: faTree as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Client Property', icon: faLocationDot as IconDefinition },
    ],
    Industrial: [
      { name: 'Office', icon: faBuildingUser as IconDefinition },
      { name: 'Factory', icon: faIndustry as IconDefinition },
      { name: 'Plant', icon: faCogs as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Distribution Centre', icon: faTruck as IconDefinition },
    ],
    'Fleet & Transport': [
      { name: 'Office', icon: faBuildingUser as IconDefinition },
      { name: 'Depot', icon: faTruck as IconDefinition },
      { name: 'Yard', icon: faTree as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Service Bay', icon: faToolbox as IconDefinition },
    ],
    'Food & Beverage': [
      { name: 'Restaurant', icon: faPlateUtensils as IconDefinition },
      { name: 'Cafe', icon: faMugHot as IconDefinition },
      { name: 'Bar / Pub', icon: faBeerMugEmpty as IconDefinition },
      { name: 'Commercial kitchen', icon: faFireBurner as IconDefinition },
      { name: 'Food truck', icon: faTruck as IconDefinition },
      { name: 'Bakery', icon: faBreadSlice as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
    ],
    'Other Industry': [
      { name: 'Office', icon: faBuildingUser as IconDefinition },
      { name: 'Warehouse', icon: faWarehouse as IconDefinition },
      { name: 'Retail', icon: faStore as IconDefinition },
      { name: 'Workshop', icon: faToolbox as IconDefinition },
      { name: 'Home', icon: faHome as IconDefinition },
      { name: 'Site', icon: faLocationDot as IconDefinition },
    ],
  };

export const DEFAULT_PLACE_TYPES: PlaceTypeSuggestion[] = [
  { name: 'Office', icon: faBuildingUser as IconDefinition },
  { name: 'House', icon: faHouse as IconDefinition },
  { name: 'Warehouse', icon: faWarehouse as IconDefinition },
  { name: 'Site', icon: faLocationDot as IconDefinition },
];

/**
 * Get place type suggestions for a specific industry
 */
export const getPlaceTypeSuggestions = (
  industry: string | null
): PlaceTypeSuggestion[] => {
  if (!industry) return DEFAULT_PLACE_TYPES;
  return INDUSTRY_PLACE_TYPES[industry as IndustryName] ?? DEFAULT_PLACE_TYPES;
};
